/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import useWebPubSubAlerts from "hooks/useWebPubSubAlerts";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

const SEVERITY_ICON_MAP = {
  critical: "notification_important",
  error: "error",
  warning: "warning",
  success: "check_circle",
  info: "notifications",
};

const getSeverityIcon = (severity = "info") => SEVERITY_ICON_MAP[severity] || "notifications";

const formatAlertTitle = (alert) => {
  if (!alert) return "Alert";
  const timestamp = alert.receivedAt ? new Date(alert.receivedAt) : null;
  const hasValidDate = timestamp instanceof Date && !Number.isNaN(timestamp?.getTime?.());
  const timeLabel = hasValidDate
    ? timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;
  const baseTitle = alert.title ?? "Alert";

  return timeLabel ? `[${timeLabel}] ${baseTitle}` : baseTitle;
};
function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const {
    alerts,
    status: alertStatus,
    error: alertError,
    clearAlerts,
  } = useWebPubSubAlerts({ instanceKey: "navbar" });
  const [hasUnreadAlerts, setHasUnreadAlerts] = useState(false);
  const previousAlertCount = useRef(0);
  const menuIsOpen = Boolean(openMenu);
  const alertCount = alerts.length;

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  useEffect(() => {
    if (menuIsOpen) {
      setHasUnreadAlerts(false);
      previousAlertCount.current = alertCount;
      return;
    }

    if (alertCount > previousAlertCount.current) {
      setHasUnreadAlerts(true);
    }

    previousAlertCount.current = alertCount;
  }, [alertCount, menuIsOpen]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => {
    setOpenMenu(event.currentTarget);
    setHasUnreadAlerts(false);
    previousAlertCount.current = alertCount;
  };
  const handleCloseMenu = () => setOpenMenu(false);

  // Render the notifications menu
  const renderMenu = () => {
    const alertsToShow = alerts.slice(0, 8);
    const isConnecting = alertStatus === "connecting" || alertStatus === "reconnecting";
    const hasError = Boolean(alertError);
    const showEmptyState = !isConnecting && !hasError && alertsToShow.length === 0;

    return (
      <Menu
        anchorEl={openMenu}
        anchorReference={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={menuIsOpen}
        onClose={handleCloseMenu}
        sx={{ mt: 2 }}
      >
        {isConnecting ? (
          <MDBox px={2.5} py={1.5} color="text.secondary">
            Connecting to alert stream...
          </MDBox>
        ) : null}
        {hasError ? (
          <MDBox px={2.5} py={1.5} color="error.main">
            Unable to load alerts.
          </MDBox>
        ) : null}
        {showEmptyState ? (
          <MDBox px={2.5} py={1.5} color="text.secondary">
            No alerts yet.
          </MDBox>
        ) : null}
        {alertsToShow.map((alert) => (
          <NotificationItem
            key={alert.id}
            icon={<Icon>{getSeverityIcon(alert.severity)}</Icon>}
            title={formatAlertTitle(alert)}
          />
        ))}
        {alerts.length > 0 ? (
          <MenuItem
            onClick={() => {
              clearAlerts();
              setHasUnreadAlerts(false);
            }}
            sx={{ justifyContent: "center", fontSize: "0.85rem", py: 1 }}
          >
            Clear alerts
          </MenuItem>
        ) : null}
      </Menu>
    );
  };
  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox pr={1}>
              <MDInput label="Search here" />
            </MDBox>
            <MDBox color={light ? "white" : "inherit"}>
              <Link to="/authentication/sign-in/basic">
                <IconButton sx={navbarIconButton} size="small" disableRipple>
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Link>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Badge color="error" variant="dot" overlap="circular" invisible={!hasUnreadAlerts}>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </Badge>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
